import {useLocation, useNavigate} from '@github-ui/react-router'
import {useScribeArtifact, useScribeArtifactsApiEnabled} from '@github-ui/scribe-artifacts'
import {useArtifactsPanelActions} from '@github-ui/scribe-artifacts/ArtifactPanelContext'
import {useEffect, useRef} from 'react'

import {ISSUE_VIEWER_NAVIGATION_BYPASS_STATE_KEY} from '../use-issue-viewer-navigation-blocker'
import {mapScribePanelArtifact} from './ArtifactsSection'

type LinkedArtifactProps = {
  number: number
  resourceId: number
  issueUrl: string
}

export function LinkedArtifact({number, resourceId, issueUrl}: LinkedArtifactProps) {
  const {selectArtifact, updateArtifact, closePanel, setClosePanelCallback} = useArtifactsPanelActions()
  const navigate = useNavigate()
  const {pathname} = useLocation()
  const enabled = useScribeArtifactsApiEnabled() && resourceId > 0
  const selectedKeyRef = useRef<string | null>(null)
  const {data} = useScribeArtifact({resourceType: 'issue', resourceId, number, enabled})

  useEffect(() => {
    // useNavigate treats absolute URLs as external and reloads the page, so pass the path.
    const issuePath = new URL(issueUrl, 'https://github.com').pathname
    // Closing stays on the same issue, so skip the unsaved-changes prompt for leaving it.
    setClosePanelCallback(() =>
      navigate(issuePath, {
        replace: true,
        state: {[ISSUE_VIEWER_NAVIGATION_BYPASS_STATE_KEY]: pathname},
      }),
    )
  }, [issueUrl, navigate, pathname, setClosePanelCallback])

  // Back/forward away from an artifact URL closes its viewer without navigating again.
  useEffect(() => {
    return () => {
      selectedKeyRef.current = null
      setClosePanelCallback(undefined)
      closePanel()
    }
  }, [closePanel, setClosePanelCallback])

  useEffect(() => {
    if (!enabled || !data) return

    const artifact = mapScribePanelArtifact(data.artifact, 0, resourceId)
    const key = `${resourceId}:${number}`
    // Open the linked artifact once per URL; later refetches only refresh its tab.
    if (selectedKeyRef.current === key) {
      updateArtifact(artifact)
    } else {
      selectedKeyRef.current = key
      selectArtifact(artifact)
    }
  }, [data, enabled, number, resourceId, selectArtifact, updateArtifact])

  return null
}
